/**
 * CMS-agnostic adapter contract.
 *
 * Implementations live in `@eneco/cms-sitecore` and `@eneco/cms-sanity`.
 * Consumers import only from `@eneco/cms` (the runtime) — never from here.
 */

import type { Image, Link, Locale, RichText } from '@eneco/cms-types';

/**
 * One field the render host must mark itself, because the overlay cannot find it.
 *
 * `kind` says how to find the rendered element; `match` is what identifies it:
 *  - `image`: the asset's file name (`<id>-<w>x<h>.<ext>`), found in an `<img>`'s `src`
 *  - `illustration`: the name it is looked up by, found on a marker the illustration lookup adds
 *
 * A field is marked only when no other field in the same block shares its `match`. Two fields
 * with one `match` — the same asset used twice in a block — are both left unmarked, because there
 * is no telling which element belongs to which: a wrong focus is worse than falling back to the
 * block.
 */
export interface CmsEditField {
  /**
   * Path of the field, same syntax as {@link CmsRendering.fieldPath}, relative to
   * {@link CmsEditField.document} when that is set and to the page's own document otherwise.
   */
  path: string;
  kind: 'image' | 'illustration';
  match: string;
  /**
   * The document the field lives in, when that is not the page's own.
   *
   * A projection such as `siteWideContent->{ … }` inlines a different document into the block. A
   * path into it means nothing in the page document — `siteWideContent.items[…]` there is a
   * reference, not the items — so the field has to say which document to open.
   */
  document?: { id: string; type: string };
}

/**
 * A piece of CMS content rendered at a given position in the page tree.
 *
 * `componentName` maps to a React component in the container's registry.
 * `content` is the flat domain object the component will receive via
 * `useContent<T>()` — fully typed at the call site.
 */
export interface CmsRendering<TContent = unknown> {
  componentName: string;
  uid?: string;
  content: TContent;
  placeholders?: Record<string, CmsRendering[]>;
  params?: Record<string, string | number | boolean | undefined>;
  /**
   * Path to this rendering inside its source document, in GROQ selector syntax
   * (`sections[_key=="a1b2"].sectionMain`). Set by providers that can express one
   * (Sanity); `undefined` everywhere else, including on every Sitecore rendering.
   *
   * Only consumed in draft mode, to build the `data-sanity` attributes Visual
   * Editing needs to map a DOM node back to a document field. See
   * `CmsEditingContext` and `libs/cms/src/placeholder.tsx`.
   */
  fieldPath?: string;
  /**
   * For each slot in `placeholders`, the GROQ path of the document array backing it —
   * but only where the slot *is* one array, in the same order, so reordering the DOM
   * and reordering the field mean the same thing. That is what makes a slot safe to
   * drag; a slot absent from this map still renders and is still click-to-editable,
   * it just has no drag target.
   *
   * Deliberately not derivable from `fieldPath`: Sanity content migrated from Sitecore
   * stores a page's blocks in one flat array, grouped into slots at render time by an
   * `area` field. Those slots are a view over the array, not the array, so a drag
   * within one would rewrite unrelated entries. See `groupPool` in
   * `libs/cms-sanity/src/adapter.ts`.
   */
  placeholderPaths?: Record<string, string>;
  /**
   * Fields of this rendering that Visual Editing cannot locate by itself, with the document
   * path to focus when one is clicked. Draft mode only.
   *
   * Text needs nothing: stega rides inside the string, so the overlay finds it. An image or an
   * illustration has no text to hide anything in — an `<img>` only offers its `alt`, which lands
   * an editor on the alt field rather than the image control, and an illustration is a lookup key
   * rendered as an SVG with no text at all. Those are listed here so the render host can put
   * `data-sanity` on the element that shows them.
   *
   * Paths come from the raw document, never from `content`: the migration folds same-shaped
   * fields into shared `role`-keyed arrays and the adapter unfolds them again for components, so a
   * key in `content` is not necessarily a key in the document. See {@link CmsEditField}.
   */
  editFields?: readonly CmsEditField[];
  /**
   * The set of sibling renderings this one may be reordered among, in draft mode. Renderings that
   * share a value are one drag-and-drop group; a rendering without one is not draggable at all.
   *
   * It exists because Sanity forms a drag group from the *array* a path points into, and a page's
   * blocks all live in one flat array (ADR-0068) whatever slot they render in. Left alone, every block
   * on the page would be one group: the overlay would draw one bounding box around the whole page and
   * guess a single drag direction from rectangles that tile in no common axis. Worse, a block dropped
   * between two blocks of a *different* slot would move in the array and change nothing on screen,
   * because its `parentKey` and `area` — which decide where it renders — are untouched.
   *
   * The value therefore names the slot: the parent block and the area within it. Within one slot,
   * order on screen is the order in the array, so moving a block past a sibling is a real reorder.
   * Absent for blocks that must not be moved: those from another document (the shared header and
   * footer), and anything with no `_key` to address.
   */
  dragGroup?: string;
}

export type CmsSource = 'sitecore' | 'sanity';

/**
 * Everything the client needs to render Sanity Visual Editing, resolved server-side.
 *
 * Present only when the request is in draft mode; `undefined` on every published
 * render, which is what the render host branches on to decide whether to mount
 * `<VisualEditing />` and emit `data-sanity` attributes.
 *
 * `studioUrl` travels here rather than through a `NEXT_PUBLIC_` variable on purpose:
 * containers are built once and run on every DTAP tier, so build-time env inlining
 * would bake in an empty string. See `config/next/config.js` (build once, run
 * everywhere) and `docs/sanity-visual-editing.md`.
 */
export interface CmsEditingContext {
  /** Absolute URL of the Sanity Studio this content is edited in. */
  studioUrl: string;
  /**
   * The Studio *workspace* this content belongs to, e.g. `acceptance`.
   *
   * Required whenever the Studio defines named workspaces, which Eneco's does — it has
   * `development` and `acceptance` and no `default`. Sanity's `createEditUrl` omits the
   * workspace segment entirely when this is absent or `'default'`, producing a link the
   * Studio answers with "Studio workspace not found".
   */
  workspace?: string;
  /** `_id` of the document that produced this page, for `createDataAttribute`. */
  documentId: string;
  /** `_type` of that document. */
  documentType: string;
  /** Path the "leave draft mode" control posts to. */
  disableDraftUrl: string;
}

export interface CmsContext {
  locale: Locale;
  pageEditing: boolean;
  pagePath: string;
  customerId?: string;
  experimentInfo?: { name: string; variant: string };
  /**
   * Which CMS adapter served this page. Set by each adapter implementation.
   * Used by the debug indicator and regression tooling.
   */
  cmsSource?: CmsSource;
  /**
   * Set when this page was fetched from the drafts perspective. Its presence is
   * the single signal that turns on Visual Editing in the render host — nothing
   * downstream reads a separate boolean.
   */
  editing?: CmsEditingContext;
}

export interface CmsPage {
  context: CmsContext;
  root: CmsRendering;
}

export interface CmsGetPageArgs {
  path: string;
  locale: Locale;
  /**
   * Dev/staging only. Forces a specific provider when a composite adapter
   * (e.g. the strangler) is active. Ignored by single-provider adapters.
   * Wired up in getServerSideProps once Phase D lands.
   */
  forceProvider?: CmsSource;
  /**
   * Fetch unpublished content. Providers that have a draft concept resolve it
   * their own way — Sanity swaps the client to `perspective: 'drafts'` with stega
   * encoding on. Providers that do not (Sitecore, whose Experience Editor decides
   * this server-side) ignore it, exactly as they already ignore `forceProvider`.
   */
  draft?: boolean;
}

export interface CmsClient {
  getPage(args: CmsGetPageArgs): Promise<CmsPage>;
  getPlaceholder(args: { path: string; placeholderName: string; locale: Locale }): Promise<CmsRendering[]>;
}

/**
 * The single seam between the renderer and a CMS provider. A new provider is
 * a single class implementing this interface — nothing else in the codebase
 * needs to change.
 */
export interface CmsAdapter {
  client: CmsClient;
  /** Maps a raw provider payload into the agnostic `CmsPage`. Pure. */
  toPage(raw: unknown): CmsPage;
  /** Maps a single raw provider rendering payload into a `CmsRendering`. Pure. */
  toRendering(raw: unknown): CmsRendering;
  toImage(raw: unknown): Image | undefined;
  toLink(raw: unknown): Link | undefined;
  toRichText(raw: unknown): RichText | undefined;
}

export { runAdapterContractTests } from './contract-test';
export { CmsPageNotFoundError } from './errors';
